import { track } from '@/common/track';
import { Utils } from '@/common/Utils';
import { StyledAccordion, StyledAccordionSummary } from '@/components/Accordion';
import { Chip } from '@/components/Chip';
import { LearningPathCourseAccordion } from '@/components/LearningPathCourseAccordion';
import { ENROLLMENT_STATUS } from '@/constants';
import { useAppData } from '@/providers/AppDataProvider';
import { useLocale } from '@/providers/LocaleProvider';
import { useSnackbar } from '@/providers/SnackbarProvider';
import IconOpenedBook from '@/resources/images/icon-opened-book.svg';
import ChevronRightIcon from '@/resources/images/icons-chevron-right.svg';
import ClockIcon from '@/resources/images/icons-clock.svg';
import PlayIcon from '@/resources/images/icons-play.svg';
import ReplaceIcon from '@/resources/images/icons-replace.svg';
import { KLAYO_COLORS } from '@/themes';
import { CourseContentSection, ShopifyCourseDetails } from '@/types/views/courses.types';
import { getEnrollmentStatusColor } from '@/utilities/enrollmentCourseStatusColor';
import { formatCurrency } from '@/utilities/formatCurrency';
import { openCertificatePdf } from '@/utilities/openCertificatePdf';
import styled from '@emotion/styled';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  AccordionDetails,
  Box,
  capitalize,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { RichText } from '@shopify/hydrogen-react';
import { CourseEvent } from 'klayowebshared';
import { isEmpty, lowerCase } from 'lodash';
import { useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from './Button';

type Props = {
  courseContentSections?: CourseContentSection[];
  onPurchaseClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  isTrainingManager?: boolean;
  course?: ShopifyCourseDetails;
  onRequestEnrolment?: () => void;
  onPurchaseMySelf?: (count: number) => void;
  isCheckoutLoading?: boolean;
  isRequestingEnrolment?: boolean;
};

// Section titles are Shopify rich-text JSON ({ type: 'root', children: [...] }); walk the tree and
// concatenate the text-node values to recover the plain-text heading.
const getPlainTextFromRichText = (raw?: string): string => {
  if (!raw) return '';
  try {
    const collect = (node: any): string => {
      if (!node) return '';
      if (typeof node.value === 'string') return node.value;
      if (Array.isArray(node.children)) return node.children.map(collect).join('');
      return '';
    };
    return collect(JSON.parse(raw)).trim();
  } catch {
    return '';
  }
};

export const CourseModulesTab = ({
  courseContentSections,
  onPurchaseClick,
  isTrainingManager,
  course,
  onRequestEnrolment,
  onPurchaseMySelf,
  isCheckoutLoading,
  isRequestingEnrolment,
}: Props) => {
  const { translate } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const { organization, user } = useAppData();

  // Non-sellable courses hide their price and all purchase/request actions. Default to sellable
  // when the flag is absent (older payloads) so existing courses are unaffected.
  const isSellable = course?.isSellable !== false;

  const [expandedModule, setExpandedModule] = useState<number | false>();

  const handleModuleChange =
    (moduleId: number, sectionTitle?: string) =>
    (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedModule(isExpanded ? moduleId : false);
      if (isExpanded) {
        const sectionName = course?.isLearningPath
          ? sectionTitle
          : getPlainTextFromRichText(sectionTitle) || `Module ${moduleId + 1}`;
        track(CourseEvent.OpenedContent, { courseName: course?.title, sectionName });
      }
    };

  const onViewCertificate = async (e: React.MouseEvent) => {
    e.stopPropagation();
    track(CourseEvent.ClickViewCertificate, { courseName: course?.title });
    if (!course?.sku) return;

    // The page describes one enrolment, so serve the certificate that enrolment earned rather than
    // the learner's newest for the course. Not for a learning path — different enrolment id space.
    await openCertificatePdf({
      sku: course.sku,
      enrollmentId: course.isLearningPath ? undefined : course.enrollmentId,
      instance: course.enrollmentInstance,
      onProcessing: () => showSnackbar(translate('myKlayo.certificateProcessing'), 'info'),
      onError: () => showSnackbar(translate('myKlayo.certificateUnavailable'), 'error'),
    });
  };

  // What a launch would actually open. For a learning path, the first launchable component (in array
  // order); relaunch picks the first launchable one, otherwise the first not yet passed/completed. For a
  // single course, this page's enrolment — but only when the server says it is launchable: an enrolment
  // LearnUpon has detached would be rejected by the launch endpoint, landing the learner on a blank
  // page. Mirrors MyLearningCard, so the card and the page it opens agree.
  const launchTarget = (relaunch: boolean) =>
    course?.isLearningPath
      ? course.learningPathComponents?.find(
          (c) =>
            c.canLaunch &&
            (relaunch ||
              ![
                ENROLLMENT_STATUS.PASSED,
                ENROLLMENT_STATUS.COMPLETED,
                ENROLLMENT_STATUS.FAILED,
              ].includes(c.status ?? ''))
        )
      : course?.canLaunch
        ? course
        : undefined;

  const handleLaunch = (e: React.MouseEvent, relaunch = false) => {
    e.stopPropagation();
    const target = launchTarget(relaunch);
    if (!target?.enrollmentId) return;
    navigate(
      `/my-klayo/enrollments/${target.enrollmentId}?instance=${target.enrollmentInstance ?? 1}`,
      {
        replace: true,
        state: {
          from: location.pathname,
          courseName: course?.title,
          // Carry CourseDetails' own origin through the enrolment excursion so it can be restored on
          // Return (this CourseDetails entry is about to be replaced), letting its Back skip the
          // iframe-polluted history.
          courseFrom: (location.state as { from?: string } | null)?.from,
        },
      }
    );
  };

  const getActions = () => {
    // A button with nowhere to go is disabled rather than hidden — the panel still describes the
    // enrolment, and a missing button would look like the page had failed to load.
    const canStart = !!launchTarget(false);
    const canRelaunch = !!launchTarget(true);

    switch (course?.enrollmentStatus) {
      case ENROLLMENT_STATUS.IN_PROGRESS:
        return (
          <Stack>
            <Button
              label={translate('common.resume')}
              variant='filled'
              disabled={!canStart}
              onClick={(e) => {
                track(CourseEvent.ClickResume, { courseName: course?.title });
                handleLaunch(e);
              }}
              endIcon={<PlayIcon />}
            />
          </Stack>
        );
      case ENROLLMENT_STATUS.PENDING_REVIEW:
        return (
          <Stack>
            <Button
              label={translate('common.resume')}
              variant='filled'
              endIcon={<PlayIcon />}
              disabled
            />
          </Stack>
        );
      case ENROLLMENT_STATUS.FAILED:
        return (
          <Stack>
            <Button
              label={translate('common.retake')}
              variant='filled'
              endIcon={<PlayIcon />}
              disabled={!canStart}
              onClick={(e) => {
                track(CourseEvent.ClickStart, { courseName: course?.title });
                handleLaunch(e);
              }}
            />
          </Stack>
        );
      case ENROLLMENT_STATUS.COMPLETED:
      case ENROLLMENT_STATUS.PASSED:
        return (
          <Stack gap={2}>
            <Button
              label={translate('common.relaunch')}
              variant='filled'
              disabled={!canRelaunch}
              onClick={(e) => {
                track(CourseEvent.ClickRelaunch, { courseName: course?.title });
                handleLaunch(e, true);
              }}
              endIcon={<ReplaceIcon />}
            />
            {course?.hasCertificate && (
              <Button
                label={translate('common.viewCertificate')}
                variant='outlined'
                onClick={onViewCertificate}
              />
            )}
          </Stack>
        );
      default:
        // Only offer "Start" (launch) when there's an actual enrolment to launch into. Without an
        // enrollmentId (e.g. an enterprise course the learner isn't enrolled in, where self-enrol
        // isn't available) we must not navigate to /enrollments/undefined.
        return course?.enrollmentId ? (
          <Stack>
            <Button
              label={translate('common.start')}
              variant='filled'
              disabled={!canStart}
              onClick={(e) => {
                track(CourseEvent.ClickStart, { courseName: course?.title });
                handleLaunch(e);
              }}
              endIcon={<PlayIcon />}
            />
          </Stack>
        ) : null;
    }
  };

  return (
    <Stack
      gap={5}
      direction={{
        xs: 'column-reverse',
        md: 'row',
      }}
    >
      {/* Left Column - Course Details */}
      <Stack gap={2} flex={1}>
        {/* Description Section */}
        {course?.description && (
          <Stack gap={2}>
            <Typography variant='h6' sx={{ fontWeight: 600, fontSize: '16px' }}>
              {translate('common.description')}
            </Typography>
            <Typography
              variant='body2'
              sx={{
                color: 'var(--grey-gr-700)',
                lineHeight: 1.6,
              }}
              dangerouslySetInnerHTML={{ __html: course.descriptionHtml }}
            />
          </Stack>
        )}

        {(course?.isLearningPath
          ? !isEmpty(course?.learningPathComponents)
          : courseContentSections && courseContentSections.length > 0) && (
          <Stack gap={2}>
            <Typography variant='h6' fontSize={18}>
              {translate('myKlayo.courseContent')}
            </Typography>

            {course?.isLearningPath ? (
              <Box
                sx={{
                  border: `1px solid ${KLAYO_COLORS.GreyGR200}`,
                  borderRadius: '16px',
                  overflow: 'hidden',
                }}
              >
                {course?.learningPathComponents?.map((component, index) => (
                  <LearningPathCourseAccordion
                    key={component.courseId}
                    index={index}
                    grouped
                    expanded={expandedModule === index}
                    onChange={handleModuleChange(index, component.title)}
                    course={{
                      ...component,
                      id: component.courseId,
                      enrollmentStatus: component.status,
                      isOverdue: component.isOverdue,
                      statusOnly: component.isStatusOnly,
                    }}
                    isTrainingManager={isTrainingManager}
                  />
                ))}
              </Box>
            ) : (
              <Stack>
                {courseContentSections?.map((section, index) => (
                  <StyledAccordion
                    key={index}
                    expanded={expandedModule === index}
                    onChange={handleModuleChange(index, section.title)}
                    elevation={0}
                  >
                    <StyledAccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`section-${index}-content`}
                      id={`section-${index}-header`}
                    >
                      <Stack>
                        <StyledSectionTitle data={section.title} />
                        <Typography
                          sx={{
                            color: 'var(--grey-gr-500) !important',
                            fontSize: '14px !important',
                            fontWeight: '400 !important',
                          }}
                        >
                          {translate('myKlayo.moduleNumber', { number: index + 1 })}
                        </Typography>
                      </Stack>
                    </StyledAccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                      <Stack gap={2}>
                        {!isEmpty(section.content) && (
                          <StyledSectionContent
                            data={section.content}
                            style={{
                              color: 'var(--grey-gr-700)',
                              lineHeight: 1.6,
                              fontSize: '16px',
                              margin: 0,
                            }}
                          />
                        )}
                        {section.buttonUrl && section.buttonLabel && (
                          <Stack width={'max-content'}>
                            <Button
                              variant='text'
                              href={section.buttonUrl}
                              target='_blank'
                              label={section.buttonLabel}
                              endIcon={<ChevronRightIcon />}
                              size='sm'
                            />
                          </Stack>
                        )}
                      </Stack>
                    </AccordionDetails>
                  </StyledAccordion>
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </Stack>

      {/* Right Column - Purchase Card */}
      <Stack
        width={{
          xs: '100%',
          md: '320px',
        }}
        gap={3}
      >
        <Box border={`1px solid ${KLAYO_COLORS.NeonBlueNB050}`} borderRadius={'12px'}>
          {course?.imageUrl ? (
            <LazyLoadImage
              style={{
                borderRadius: '12px',
                aspectRatio: '10/7',
                width: '100%',
                objectFit: 'cover',
              }}
              src={course.imageUrl}
              effect='blur'
              width={'100%'}
            />
          ) : (
            <Box
              sx={{
                aspectRatio: '10/7',
                width: '100%',
                borderRadius: '12px',
                background: `linear-gradient(360deg, ${KLAYO_COLORS.GreyGR200} 0%, ${KLAYO_COLORS.WhiteWH900} 100%)`,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flex: 1,
              }}
            >
              <IconOpenedBook width={80} height={80} color={KLAYO_COLORS.GreyGR200} />
            </Box>
          )}
        </Box>

        {!isTrainingManager && course?.enrollmentStatus ? (
          <>
            <Stack width={'max-content'}>
              <Chip
                backgroundColor={getEnrollmentStatusColor(course.enrollmentStatus)}
                textColor={KLAYO_COLORS.WhiteWH900}
                label={capitalize(lowerCase(course.enrollmentStatus))}
              />
            </Stack>

            {![ENROLLMENT_STATUS.COMPLETED, ENROLLMENT_STATUS.PASSED].includes(
              course.enrollmentStatus
            ) ? (
              <Stack direction='column' gap={1}>
                <Typography variant='body2' sx={{ color: 'var(--grey-gr-500)' }}>
                  {translate('myKlayo.lastActivityDate', {
                    date: course.lastActivity
                      ? Utils.formatDate(course.lastActivity)
                      : translate('myKlayo.never'),
                  })}
                </Typography>
                {course.dueDate ? (
                  <>
                    {!course.isOverdue ? (
                      <Typography variant='body2' sx={{ color: 'var(--grey-gr-500)' }}>
                        {Utils.getDaysBetweenDates(new Date(), new Date(course.dueDate)) === 0
                          ? translate('myKlayo.dueToday', {
                              date: Utils.formatDateWithoutToday(course.dueDate),
                            })
                          : translate('myKlayo.dueInDays', {
                              days: Utils.getDaysBetweenDates(new Date(), new Date(course.dueDate)),
                              date: Utils.formatDateWithoutToday(course.dueDate),
                            })}
                      </Typography>
                    ) : (
                      <Typography variant='body2' color={KLAYO_COLORS.TouchRedTR500}>
                        {Utils.getDaysBetweenDates(new Date(), new Date(course.dueDate)) === 0
                          ? translate('myKlayo.overdueToday', {
                              date: Utils.formatDateWithoutToday(course.dueDate),
                            })
                          : translate('myKlayo.overdueByDays', {
                              days: Math.abs(
                                Utils.getDaysBetweenDates(new Date(), new Date(course.dueDate))
                              ),
                              date: Utils.formatDateWithoutToday(course.dueDate),
                            })}
                      </Typography>
                    )}
                  </>
                ) : null}
              </Stack>
            ) : course.dateCompleted ? (
              <Typography variant='body2' sx={{ color: 'var(--grey-gr-500)' }}>
                {translate('myKlayo.completedOn', {
                  date: Utils.formatDate(course.dateCompleted),
                })}
              </Typography>
            ) : null}

            <Stack gap={1}>
              {course?.isLearningPath ? (
                <Typography variant='body2' color={KLAYO_COLORS.GreyGR500}>
                  {translate('myKlayo.numberOfCompletedCourses', {
                    count: course.numberCompletedCourses,
                    total: course.learningPathComponents?.length,
                  })}
                </Typography>
              ) : (
                <Typography variant='body2' color={KLAYO_COLORS.GreyGR500}>
                  {course.score
                    ? translate('myKlayo.score', { score: course.score })
                    : translate('myKlayo.percentageCompleted', {
                        percentageCompleted: course.percentageCompleted ?? 0,
                      })}
                </Typography>
              )}
              <LinearProgress
                value={course.percentageCompleted}
                variant='determinate'
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: KLAYO_COLORS.GreyGR200,
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3,
                    backgroundColor: KLAYO_COLORS.GreenGR600,
                  },
                }}
              />
            </Stack>

            {getActions()}

            {course?.courseAccessExpiresAt && (
              <Typography
                variant='body2'
                sx={{
                  color: 'var(--grey-gr-500)',
                }}
              >
                {translate('myKlayo.courseAvailableUntilDate', {
                  date: Utils.formatDate(course.courseAccessExpiresAt),
                })}
              </Typography>
            )}
          </>
        ) : (
          <>
            {/* 2nd-LMS (own LearnUpon) courses are never purchasable, so they show no price.
                Ceventas courses are unchanged. Non-sellable courses hide the price too. */}
            {isSellable && !course?.isExternalCatalogCourse && (
              <Stack gap={1}>
                <Typography variant='h5'>{formatCurrency(course?.priceInCents || 0, 2)}</Typography>
                {!!course?.discountPercent && (
                  <Chip
                    label={`${course?.discountPercent}% off when buying with credits`}
                    backgroundColor={KLAYO_COLORS.GreyGR100}
                    textColor={KLAYO_COLORS.GreyGR900}
                    sx={{
                      justifyContent: 'flex-start',
                    }}
                  />
                )}
              </Stack>
            )}

            {!!course?.duration && (
              <Stack gap={1} direction={'row'} alignItems={'center'}>
                <ClockIcon width={20} height={20} color={KLAYO_COLORS.GreyGR500} />
                <Typography variant='body2' sx={{ color: 'var(--grey-gr-900)' }}>
                  {translate('myKlayo.completeTime', { duration: course?.duration })}
                </Typography>
              </Stack>
            )}

            {isSellable &&
              !course?.isExternalCatalogCourse &&
              (course?.purchaseOptions.hasPendingEnrolmentRequest ? (
                <>
                  <Button variant='filled' label={translate('myKlayo.requestPending')} disabled />

                  <Typography variant='body2' sx={{ color: KLAYO_COLORS.TouchRedTR600 }}>
                    {translate('myKlayo.enrolmentRequestPending')}
                  </Typography>
                </>
              ) : organization?.isPaidPlan && !isTrainingManager ? (
                <Stack gap={1.5}>
                  {!user?.isTrainingManager && course?.purchaseOptions?.canRequestEnrolment && (
                    <Button
                      variant='filled'
                      label={translate('myKlayo.requestEnrolment')}
                      onClick={onRequestEnrolment}
                      disabled={isRequestingEnrolment}
                      isLoading={isRequestingEnrolment}
                    />
                  )}

                  <Button
                    variant='outlined'
                    label={translate('myKlayo.purchaseForMyself')}
                    onClick={() => onPurchaseMySelf?.(1)}
                    disabled={isCheckoutLoading}
                    isLoading={isCheckoutLoading}
                  />
                </Stack>
              ) : (
                <>
                  {isTrainingManager ? (
                    user?.isAllowEnrollStudents ? (
                      <Button
                        variant='filled'
                        label={translate('manage.enrollStudents')}
                        onClick={onPurchaseClick}
                      />
                    ) : null
                  ) : (
                    <Button
                      variant='filled'
                      label={translate('coursesPage.buyNow')}
                      onClick={onPurchaseClick}
                    />
                  )}

                  <Typography variant='body2' sx={{ color: 'var(--grey-gr-500)' }}>
                    {translate('myKlayo.multiplePurchaseOptions')}
                  </Typography>
                </>
              ))}

            {/* 2nd-LMS (own LearnUpon) courses are never purchasable, but the org may allow
                enrolment requests (approved by a TM, who enrols the learner into that portal). */}
            {isSellable &&
              course?.isExternalCatalogCourse &&
              (course?.purchaseOptions?.hasPendingEnrolmentRequest ? (
                <>
                  <Button variant='filled' label={translate('myKlayo.requestPending')} disabled />
                  <Typography variant='body2' sx={{ color: KLAYO_COLORS.TouchRedTR600 }}>
                    {translate('myKlayo.enrolmentRequestPending')}
                  </Typography>
                </>
              ) : course?.purchaseOptions?.canRequestEnrolment && !user?.isTrainingManager ? (
                <Button
                  variant='filled'
                  label={translate('myKlayo.requestEnrolment')}
                  onClick={onRequestEnrolment}
                  disabled={isRequestingEnrolment}
                  isLoading={isRequestingEnrolment}
                />
              ) : null)}
          </>
        )}
      </Stack>
    </Stack>
  );
};

const StyledSectionTitle = styled(RichText)({
  '*': {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: 'var(--grey-gr-900)',
  },
});
const StyledSectionContent = styled(RichText)({
  '*': {
    margin: 0,
    fontSize: '16px',
    fontWeight: 400,
    color: 'var(--grey-gr-700)',
  },
});
